import { useRef, useState } from 'react';
import PosLandingReveal from './PosLandingReveal';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import {
  LANDING_DATA_RETENTION, LANDING_PRODUCT_CAP, LANDING_SECTIONS,
} from '../constants/posLandingConst';

/**
 * The questions, in order. Built per render because two answers quote live
 * prices from Admin Settings.
 *
 * @param {{extraStaff: string, warehouse: string}} price - formatted
 * @returns {Array<{question: string, answer: JSX.Element}>}
 */
const buildQuestions = (price) => [
  {
    question: 'What happens when my Free Demo or Plan expires?',
    answer: (
      <>
        <p>When your plan expires you temporarily lose access to the POS system, but your data is not deleted straight away.</p>
        <ul>
          <li><b>Your data is safe for {LANDING_DATA_RETENTION}</b> — all your sales history, inventory and staff accounts stay securely stored during this time.</li>
          <li><b>Seamless restoration</b> — subscribe within those {LANDING_DATA_RETENTION} and your access is restored instantly — you carry on exactly where you left off.</li>
          <li><b>Data deletion</b> — after {LANDING_DATA_RETENTION} without an active subscription, your data is permanently erased.</li>
        </ul>
      </>
    ),
  },
  {
    question: 'Will there be a limit on the number of products we can add to our inventory?',
    answer: (
      <p>There is no overall limit on the number of products you can add. To prevent abuse and protect our data storage we apply an initial cap of {LANDING_PRODUCT_CAP} products. If you need more than that, reach out to our team and we can raise your limit.</p>
    ),
  },
  {
    question: 'Can I run more than one branch?',
    answer: (
      <>
        <p>Yes. Our Enterprise option lets you run multiple branches, with every location connected to one central dashboard that gives you real-time access to:</p>
        <ul>
          <li><b>Live sales data per branch</b></li>
          <li><b>Individual branch inventory</b></li>
          <li><b>Centralised warehouse and reserve stocks</b></li>
        </ul>
      </>
    ),
  },
  {
    question: 'Can I add a warehouse to pull stock from and move it to the store?',
    answer: (
      <p>Yes. For an additional {price.warehouse} you can activate this feature: click <b>Add a warehouse</b> in your Inventory panel and complete the payment. The warehouse is embedded directly into your system and works alongside your store inventory, so stock transfers are straightforward.</p>
    ),
  },
  {
    question: 'Can I add more staff accounts, and what is the purpose of having an additional one?',
    answer: (
      <>
        <p>Yes, you can add an unlimited number of staff accounts for {price.extraStaff} each. Extra staff accounts keep your records accurate by giving you:</p>
        <ul>
          <li><b>Shift management</b> — manage employees on different schedules by giving each one their own exclusive access.</li>
          <li><b>Individual tracking</b> — monitor each staff member&apos;s sales, inventory actions and transaction history separately.</li>
        </ul>
      </>
    ),
  },
  {
    question: 'What happens when my internet drops?',
    answer: (
      <p>Your existing data is perfectly safe if you lose internet access. Any new actions you take while disconnected will not be saved, because the system relies on an active connection to update your account in real time.</p>
    ),
  },
];

/**
 * Frequently asked questions, as an accordion that keeps one answer open at
 * a time.
 *
 * @param {object} props
 * @param {{extraStaffPrice: number, warehousePrice: number}} props.prices
 * @returns {JSX.Element}
 */
function PosLandingFaq({ prices }) {
  const { config } = useConfig();
  const [open, setOpen] = useState(null);
  const answerRefs = useRef([]);

  const questions = buildQuestions({
    extraStaff: formatMoney(prices.extraStaffPrice, config),
    warehouse: formatMoney(prices.warehousePrice, config),
  });

  return (
    <section id={LANDING_SECTIONS.faq}>
      <div className="wrap faq-grid">
        <PosLandingReveal className="head" delay={140}>
          <h2>Frequently Asked Questions</h2>
          <p>The things every owner asks before moving their counter to something new.</p>
        </PosLandingReveal>
        <div className="acc">
          {questions.map((item, index) => {
            const expanded = open === index;
            return (
              <div className="acc-item" key={item.question}>
                <button
                  type="button"
                  className="acc-q"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : index)}
                >
                  {item.question}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
                <div
                  className="acc-a"
                  ref={(element) => { answerRefs.current[index] = element; }}
                  style={{ maxHeight: expanded ? `${answerRefs.current[index]?.scrollHeight || 0}px` : '0px' }}
                >
                  {item.answer}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PosLandingFaq;
